import { useEffect, useRef } from "react";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { useThemes, useStyles, themeName } from "../../hooks/useStyles";
import { Button } from "../../components/ui/Button";
import { ThemeCard } from "./ThemeCard";

/**
 * STAGE · ThemeSelect (HERIT 01) — 540×820 카드 카루셀. 가운데 스냅된 카드가 곧 선택이다.
 * 양옆 카드는 흐리게(opacity·blur) 깔리고, 탭하면 가운데로 스크롤해 선택된다. 가운데 카드를 다시 탭하면 바로 진행.
 * 도트 페이저가 위치를, 하단 CTA 가 진행을 맡는다. 폰은 카드 폭만 줄어들고 구조는 같다.
 */
export function ThemeSelectView() {
  const go = useRouter((s) => s.go);
  const selectedId = useRouter((s) => s.selectedThemeId);
  const setTheme = useRouter((s) => s.setTheme);
  const setStyle = useRouter((s) => s.setStyle);
  const { t, language } = useI18n();
  const themes = useThemes();
  const styles = useStyles();
  const railRef = useRef<HTMLDivElement | null>(null);
  // 프로그램 스크롤(초기 위치 맞춤) 중에는 IntersectionObserver 가 선택을 되돌리지 않게
  const settlingRef = useRef(true);

  // 진입 시 선택이 없거나 목록에 없는 테마면 첫 테마를 기본 선택
  useEffect(() => {
    if (themes.length === 0) return;
    if (!selectedId || !themes.some((th) => th.id === selectedId)) {
      setTheme(themes[0]!.id);
    }
  }, [themes, selectedId, setTheme]);

  const selectedIndex = Math.max(0, themes.findIndex((th) => th.id === selectedId));
  const selected = themes[selectedIndex] ?? null;

  // 마운트 시 선택 카드를 가운데로 (애니메이션 없이) — 그 뒤부터 사용자 스크롤을 선택으로 반영
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !selected) return;
    const el = rail.querySelector<HTMLElement>(`[data-theme-id="${selected.id}"]`);
    el?.scrollIntoView({ behavior: "instant", inline: "center", block: "nearest" });
    const id = window.setTimeout(() => {
      settlingRef.current = false;
    }, 150);
    return () => window.clearTimeout(id);
    // 초기 위치만 — 선택이 바뀔 때마다 다시 스크롤하면 사용자 스와이프와 싸운다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes.length]);

  // 레일 가운데(60% 이상 노출)에 들어온 카드 = 선택. 스냅이 끝나면 정확히 한 장만 이 조건을 만족한다
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (settlingRef.current) return;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const id = (e.target as HTMLElement).dataset.themeId;
          if (id && id !== useRouter.getState().selectedThemeId) {
            setTheme(id);
            setStyle(null);
          }
        }
      },
      { root: rail, threshold: 0.6 },
    );
    rail.querySelectorAll<HTMLElement>("[data-theme-id]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [themes, setTheme, setStyle]);

  const scrollTo = (id: string) => {
    railRef.current
      ?.querySelector<HTMLElement>(`[data-theme-id="${id}"]`)
      ?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  const proceed = (id: string) => {
    if (id !== selectedId) {
      setTheme(id);
      // 테마가 바뀌면 이전 테마의 스타일 선택은 무효 — 스타일 화면이 첫 스타일을 다시 고른다
      setStyle(null);
    }
    go("styleSelect");
  };

  return (
    <div className="view view--theme">
      <h1 className="view__title">{t.themeSelect.title}</h1>

      <div className="view__body">
        <div ref={railRef} className="theme-rail no-scrollbar" role="listbox" aria-label={t.journey.theme}>
          {themes.map((th, i) => (
            <ThemeCard
              key={th.id}
              theme={th}
              index={i}
              total={themes.length}
              styles={styles}
              active={th.id === selected?.id}
              language={language}
              onSelect={() => {
                if (th.id === selectedId) {
                  // 가운데(선택된) 카드를 다시 누르면 바로 진행 — 키오스크에서 탭 두 번으로 넘어가게
                  proceed(th.id);
                } else {
                  setTheme(th.id);
                  setStyle(null);
                  scrollTo(th.id);
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* 도트 페이저 — 탭하면 그 카드로 스크롤·선택 */}
      <ol className="dots" aria-label={t.themeSelect.count(themes.length)}>
        {themes.map((th, i) => (
          <li key={th.id} aria-current={i === selectedIndex ? "true" : undefined}>
            <button
              type="button"
              aria-label={themeName(th, language)}
              onClick={() => {
                setTheme(th.id);
                setStyle(null);
                scrollTo(th.id);
              }}
            />
          </li>
        ))}
      </ol>

      <div className="view__cta">
        <Button disabled={!selected} onClick={() => selected && proceed(selected.id)}>
          {t.themeSelect.cta}
        </Button>
      </div>
    </div>
  );
}
